// MapPage.jsx
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import door from "../../images/door.png";
import { URL } from "../../utils/constants";

import {
  YMaps,
  Map as YMap,
  Placemark,
  Polyline,
  ObjectManager,
  RulerControl,
  Polygon,
  TypeSelector,
  Clusterer,
} from "@pbe/react-yandex-maps";

import { getDistance } from "geolib";
import DropdownMenu from "./dropdown";
import DrawPanel from "./DrawPanel";
import FiltersPanel from "./FiltersPanel";
import DrawPointOCKPanel from "./DrawPointOCKPanel";
import OckClonePanel from "./OckClonePanel";
import {
  ArchiveBuildingsLayer,
  ArchiveBuildingsPanel,
} from "../archive-buildings/ArchiveBuildings";

import ToolsDrawer, { ToolItem } from "./ToolsDrawer";
import "./MapPage.css";

/* ------------------------- proj4 (CDN) + МГГТ ------------------------- */
let __MGGT_DEFINED__ = false;
let __PROJ4_WARN_SHOWN__ = false;

function getProj4() {
  if (typeof window !== "undefined" && window.proj4) {
    if (!__MGGT_DEFINED__) {
      try {
        window.proj4.defs(
          "MGGT",
          "+proj=tmerc +lat_0=55.66666666667 +lon_0=37.5 +k=1 " +
            "+x_0=11.86143 +y_0=12.1761150 +ellps=bessel " +
            "+towgs84=316.151,78.924,589.65,-1.57273,2.69209,2.34693,8.4507 " +
            "+units=m +no_defs"
        );
        __MGGT_DEFINED__ = true;
      } catch {}
    }
    return window.proj4;
  }
  return null;
}

const toLonLatPair = (pair) => {
  const [a, b] = pair;
  if (typeof a === "number" && typeof b === "number") {
    if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [b, a];
  }
  return [a, b];
};

const normalizeForExport = (coords, { isPolygon = false } = {}) => {
  if (!coords) return [];
  if (Array.isArray(coords[0])) {
    if (Array.isArray(coords[0][0])) {
      const ring = coords[0];
      const ll = ring.map(toLonLatPair);
      if (isPolygon && ll.length > 2) {
        const first = ll[0];
        const last = ll[ll.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) ll.push([...first]);
      }
      return ll;
    } else if (coords[0].length === 2) {
      const ll = coords.map(toLonLatPair);
      if (isPolygon && ll.length > 2) {
        const first = ll[0];
        const last = ll[ll.length - 1];
        if (first[0] !== last[0] || first[1] !== last[1]) ll.push([...first]);
      }
      return ll;
    }
  }
  return [toLonLatPair(coords)];
};

function wgs84ToMggtNE(pair) {
  const p4 = getProj4();
  const [lon, lat] = toLonLatPair(pair);
  if (p4) {
    const [E, N] = p4("WGS84", "MGGT", [lon, lat]);
    return [N, E];
  }

  if (!__PROJ4_WARN_SHOWN__) {
    try {
      let box = document.getElementById("gm-toast-box");
      if (!box) {
        box = document.createElement("div");
        box.id = "gm-toast-box";
        document.body.appendChild(box);
      }
      const t = document.createElement("div");
      t.className = "gm-toast";
      t.textContent = "proj4 не найден — копирую в WGS84 (широта,долгота)";
      box.appendChild(t);
      setTimeout(() => t.classList.add("fade"), 1200);
      setTimeout(() => {
        try {
          box.removeChild(t);
        } catch {}
      }, 2100);
    } catch {}
    __PROJ4_WARN_SHOWN__ = true;
  }
  return [lat, lon];
}

function formatMggtForCopy(coords, { isPolygon = false } = {}) {
  const ll = normalizeForExport(coords, { isPolygon });
  return ll
    .map(([lon, lat]) => {
      const [N, E] = wgs84ToMggtNE([lat, lon]);
      return `${Number(N).toFixed(3)},${Number(E).toFixed(3)}`;
    })
    .join("\n");
}
/* --------------------------------------------------------------------- */

const MapPage = (userState) => {
  const u = userState?.user ?? userState ?? {};
  const username = String(
    u?.username ??
      u?.user?.username ??
      (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "{}")?.username;
        } catch (e) {
          return null;
        }
      })() ??
      localStorage.getItem("username") ??
      ""
  )
    .trim()
    .toLowerCase();

  const isGuest = username === "guest";
  const token = localStorage.getItem("auth_token");

  const norm = (v) => String(v ?? "").trim().toLowerCase();

  const pickGroupName = (g) => {
    if (!g) return "";
    if (typeof g === "string") return g;
    if (typeof g === "object") return g.name || g.title || g.slug || g.code || "";
    return "";
  };

  const normalizeGroups = (groupsLike) => {
    if (!groupsLike) return [];
    if (Array.isArray(groupsLike)) return groupsLike.map(pickGroupName).map(norm).filter(Boolean);

    if (typeof groupsLike === "string") {
      return groupsLike
        .split(",")
        .map((s) => norm(s))
        .filter(Boolean);
    }

    if (typeof groupsLike === "object") {
      const arr = groupsLike.results || groupsLike.items || [];
      if (Array.isArray(arr)) return arr.map(pickGroupName).map(norm).filter(Boolean);
    }

    return [];
  };

  const inGroup = (name) => {
    const n = norm(name);
    return (userGroups || []).some((g) => g === n);
  };

  const anyGroup = (names = []) => names.some((n) => inGroup(n));

  const [userId, setUserId] = useState(null);
  const [isStaff, setIsStaff] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [userGroups, setUserGroups] = useState([]);

  useEffect(() => {
    try {
      const uParsed = JSON.parse(localStorage.getItem("user") || "{}");
      if (uParsed?.id) setUserId(uParsed.id);
      if (typeof uParsed?.is_staff === "boolean") setIsStaff(uParsed.is_staff);
      if (typeof uParsed?.is_superuser === "boolean") setIsSuperuser(uParsed.is_superuser);
      setUserGroups(normalizeGroups(uParsed?.groups));
    } catch {}

    if (!token) return;

    (async () => {
      try {
        const r = await fetch(`${URL}/api/me/`, {
          headers: { Authorization: `Token ${token}` },
        });

        if (r.status === 401) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("user");
          localStorage.removeItem("username");

          setUserId(null);
          setIsStaff(false);
          setIsSuperuser(false);
          setUserGroups([]);
          return;
        }

        if (!r.ok) return;

        const data = await r.json();
        if (data?.id && !userId) setUserId(data.id);
        if (typeof data?.is_staff === "boolean") setIsStaff(data.is_staff);
        if (typeof data?.is_superuser === "boolean") setIsSuperuser(data.is_superuser);
        setUserGroups(normalizeGroups(data?.groups));
      } catch (e) {
        console.error("Failed to load current user:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAdmin = !!(isStaff || isSuperuser);

  const canViewBuildings = isAdmin || inGroup("buildings");
  const canUseNavigation = isAdmin || inGroup("hto");
  const canViewArchiveBuildings =
    isAdmin || inGroup("arch_build") || inGroup("arch_admin");
  const canManageArchiveBuildings = isAdmin || inGroup("arch_admin");

  const mainOck = isAdmin || inGroup("ock");
  const canManageOck = mainOck || inGroup("creator_ock");
  const canViewOck = canManageOck || inGroup("point_ock");

  const strictOckAccess = isAdmin || inGroup("strict point_ock");
  const hasOnlyAdd = isAdmin || inGroup("only add");

  const canCloneOck = canManageOck && !isGuest;

  const [tasksOpen, setTasksOpen] = useState(false);

  const [coordinates, setCoordinates] = useState({ details: [] });
  const [linecoordinates, setLinecoordinates] = useState({ details: [] });
  const [hermodoorcoord, setHermodoorcoord] = useState({ details: [] });
  const [buildingscoord, setBuildingscoord] = useState({ details: [] });
  const [crossway, setCrossway] = useState({ details: [] });
  const [wayoutscoord, setWayoutscoord] = useState({ details: [] });
  const [depocoord, setDepocoord] = useState({ details: [] });
  const [metromostcoord, setMetromostcoord] = useState({ details: [] });
  const [pointsOckCoord, setPointsOckCoord] = useState({ details: [] });
  const [archiveBuildingsCoord, setArchiveBuildingsCoord] = useState({ details: [] });
  const [archiveBuildingEditRequest, setArchiveBuildingEditRequest] = useState(null);
  const [activeStationId, setActiveStationId] = useState(null);

  // фильтр точек ОСК
  const [ockMatchIds, setOckMatchIds] = useState(null); // null | number[]
  const ockMatchSet = useMemo(() => {
    if (!ockMatchIds) return null;
    return new Set(ockMatchIds.map(Number));
  }, [ockMatchIds]);

  const handleOckMatchChange = useCallback((idsOrNull) => {
    setOckMatchIds(idsOrNull); // idsOrNull: null | number[]
  }, []);

  const polygonsRef = useRef({});
  const linesRef = useRef({});
  const linePolyRefs = useRef({});
  const placemarksRef = useRef({});
  const mapRef = useRef(null);
  const buildingsClustererRef = useRef(null);
  const isClusterZoomingRef = useRef(false);

  const balloonActionsRef = useRef(new Map());

  const POINT_DUPLICATE_RADIUS_METERS = 1;

  const TOOL_SECTIONS = {
    DRAW: "draw",
    OCK: "ock",
    FILTERS: "filters",
    ARCHIVE_BUILDINGS: "archive-buildings",
  };

  const EXCLUSIVE_TOOLS = {
    DRAW: "draw",
    OCK_PICK: "ock-pick",
    OCK_CLONE: "ock-clone",
  };

  const [activeExclusiveTool, setActiveExclusiveTool] = useState(null);
  const activeExclusiveToolRef = useRef(null);
  const exclusiveToolStopsRef = useRef(new Map());

  const splitOckItemsByMatch = useCallback(
    (items) => {
      if (!ockMatchSet) {
        return {
          bright: items,
          faded: [],
        };
      }

      const bright = [];
      const faded = [];

      for (const p of items) {
        if (ockMatchSet.has(Number(p.id))) bright.push(p);
        else faded.push(p);
      }

      return { bright, faded };
    },
    [ockMatchSet]
  );

  useEffect(() => {
    const handler = (e) => {
      const el = e.target?.closest?.("[id^='gm-']");
      if (!el) return;

      const id = el.id;
      const fn = balloonActionsRef.current.get(id);
      if (!fn) return;

      e.preventDefault();
      e.stopPropagation();
      fn();
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    const getToolbarEl = () =>
      document.getElementById("topToolbar") || document.querySelector(".dd-toolbar");

    const apply = () => {
      const el = getToolbarEl();
      if (!el) {
        root.style.setProperty("--toolbarH", "0px");
        return;
      }
      const rect = el.getBoundingClientRect();
      const bottom = Math.max(0, Math.round(rect.bottom));
      root.style.setProperty("--toolbarH", `${bottom}px`);
    };

    apply();

    let ro = null;
    const el = getToolbarEl();
    if (el && window.ResizeObserver) {
      ro = new ResizeObserver(apply);
      ro.observe(el);
    }

    window.addEventListener("resize", apply);
    window.visualViewport?.addEventListener?.("resize", apply);
    window.visualViewport?.addEventListener?.("scroll", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.visualViewport?.removeEventListener?.("resize", apply);
      window.visualViewport?.removeEventListener?.("scroll", apply);
      ro?.disconnect?.();
    };
  }, []);

  const toggleTool = (key) => setToolOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const [isOckPicking, setIsOckPicking] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [toolOpen, setToolOpen] = useState({
    [TOOL_SECTIONS.DRAW]: false,
    [TOOL_SECTIONS.OCK]: false,
    [TOOL_SECTIONS.FILTERS]: false,
    [TOOL_SECTIONS.ARCHIVE_BUILDINGS]: false,
  });

  const [ockCloneMode, setOckCloneMode] = useState({
    active: false,
    sourcePointId: null,
    points: [], // [{ tempId, lat, long, address, inventory_number }]
  });

  const ockCloneModeRef = useRef({
    active: false,
    sourcePointId: null,
    points: [],
  });

  useEffect(() => {
    ockCloneModeRef.current = ockCloneMode;
  }, [ockCloneMode]);

  const ockCloneTempRefs = useRef([]);
  const ockCloneHandlersRef = useRef([]);
  const ockCloneTempIdRef = useRef(1);
  const [ockCloneSaving, setOckCloneSaving] = useState(false);

  const sourceClonePoint = useMemo(() => {
    if (!ockCloneMode.sourcePointId) return null;

    return (pointsOckCoord.details || []).find(
      (p) => Number(p.id) === Number(ockCloneMode.sourcePointId)
    ) || null;
  }, [ockCloneMode.sourcePointId, pointsOckCoord.details]);

  const ockPointsForRender = useMemo(() => {
    const all = pointsOckCoord.details || [];
    if (strictOckAccess) return all;
    return all.filter((p) => p?.is_visible !== false);
    
  }, [pointsOckCoord.details, strictOckAccess]);

  const lastBalloonIdRef = useRef(null);

  const [mobileToolbarOpen, setMobileToolbarOpen] = useState(false);
  const [mobileToolsDrawerOpen, setMobileToolsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const toolsDrawerOpen = isMobile ? mobileToolsDrawerOpen : toolsOpen;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener?.("change", apply) ?? mq.addListener(apply);
    return () => mq.removeEventListener?.("change", apply) ?? mq.removeListener(apply);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.toggleAttribute("data-gm-mobile", !!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const handler = () => {
      if (!isMobile) return;
      setMobileToolbarOpen((v) => !v);
    };
    window.addEventListener("gm:toggleMobileToolbar", handler);
    return () => window.removeEventListener("gm:toggleMobileToolbar", handler);
  }, [isMobile]);

  useEffect(() => {
    const handler = () => {
      if (!isMobile) return;
      setMobileToolsDrawerOpen((v) => !v);
      setMobileToolbarOpen(false);
    };

    window.addEventListener("gm:toggleMobileToolsDrawer", handler);
    return () => window.removeEventListener("gm:toggleMobileToolsDrawer", handler);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      setToolsOpen(false);
    } else {
      setMobileToolsDrawerOpen(false);
    }
  }, [isMobile]);

  const toggleMobileToolbar = () => setMobileToolbarOpen((v) => !v);

  const startArchiveBuildingPositionCorrection = useCallback((buildingId) => {
    if (!canManageArchiveBuildings) return;
    setArchiveBuildingEditRequest(String(buildingId));
    setToolOpen((current) => ({
      ...current,
      [TOOL_SECTIONS.ARCHIVE_BUILDINGS]: true,
    }));
    if (isMobile) {
      setMobileToolsDrawerOpen(true);
      setMobileToolbarOpen(false);
    } else {
      setToolsOpen(true);
    }
  }, [canManageArchiveBuildings, isMobile, TOOL_SECTIONS.ARCHIVE_BUILDINGS]);

  const clearArchiveBuildingEditRequest = useCallback(() => {
    setArchiveBuildingEditRequest(null);
  }, []);

  const ymapsApiRef = useRef(null);

  const [visible, setVisible] = useState([]);
  const [mode, setMode] = useState("linesections");

  const [activeIds, setActiveIds] = useState({
    activeStations: [],
    activeLines: [],
    activeDiameters: [],
  });

  const getOckPointColorKey = (p) => {
    if (strictOckAccess && p?.is_visible === false) return "hidden";

    switch (p?.status) {
      case "created":
        return "created";
      case "in_work":
        return "in_work";
      case "ready":
        return "ready";
      case "done":
        return "done";
      default:
        return "created";
    }
  };

  const OCK_COLORS = {
    created: "#ff0000",
    in_work: "#ffee00",
    ready: "#00ff00",
    done: "#1100ff",
    hidden: "#95a5a6",
  };

  const makeCircleSvgUrl = (hex, opacity = 1, size = 22) => {
    const ringColor = String(hex || "#000").replace("#", "");
    const c = size / 2;
    const r = size === 27 ? 10 : 8;
    const strokeWidth = size === 27 ? 4 : 3.5;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
        <circle
          cx="${c}"
          cy="${c}"
          r="${r}"
          fill="#ffffff"
          fill-opacity="${opacity}"
          stroke="#${ringColor}"
          stroke-width="${strokeWidth}"
          stroke-opacity="${opacity}"
        />
      </svg>
    `;

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const makeClusterSvgUrl = (hex, opacity = 1, size = 35) => {
    const ringColor = String(hex || "#000").replace("#", "");
    const c = size / 2;
    const r = 10;
    const strokeWidth = 4;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
        <circle
          cx="${c}"
          cy="${c}"
          r="${r}"
          fill="#ffffff"
          fill-opacity="${opacity}"
          stroke="#${ringColor}"
          stroke-width="${strokeWidth}"
          stroke-opacity="${opacity}"
        />
      </svg>
    `;

    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const metroLineColors = {
    Сокольническая: "EF161E",
    Замоскворецкая: "2DBE2C",
    "Арбатско-Покровская": "0078BE",
    Филевская: "00BFFF",
    Кольцевая: "8D5B2D",
    "Калужско-Рижская": "ED9121",
    "Таганско-Краснопресненская": "800080",
    Калининская: "FFD702",
    Солнцевская: "FFD702",
    "Серпуховско-Тимирязевская": "999999",
    "Люблинско-Дмитровская": "99CC00",
    "Большая кольцевая": "82C0C0",
    Бутовская: "A1B3D4",
    "Московское центральное кольцо": "FFFFFF",
    Троицкая: "03795F",
    "Рублево-Архангельская": "27303F",
    Некрасовская: "DE64A1",
    БКЛ: "82C0C0",
    "Большая кольцевая линия метро": "82C0C0",
    D1: "B5AAA0",
    D2: "BFABBC",
    D3: "E9E6E2",
    D4: "80918B",
  };

  const isDLineName = (line) => ["D1", "D2", "D3", "D4"].includes(String(line).trim());

  const metroLineNumbers = {
    Сокольническая: 1,
    Замоскворецкая: 2,
    "Арбатско-Покровская": 3,
    Филевская: 4,
    Кольцевая: 5,
    "Калужско-Рижская": 6,
    "Таганско-Краснопресненская": 7,
    Калининская: 8,
    "Серпуховско-Тимирязевская": 9,
    "Люблинско-Дмитровская": 10,
    БКЛ: 11,
    Бутовская: 12,
    "Московский монорельс": 13,
    "Московское центральное кольцо": 14,
    Некрасовская: 15,
    Троицкая: 16,
    "Рублёво-Архангельская": 17,
    Бирюлёвская: 18,
    Власьевская: 19,
    D1: "D1",
    D2: "D2",
    D3: "D3",
    D4: "D4",
  };

  useEffect(() => {
    if (document.getElementById("gm-global-style")) return;
    const style = document.createElement("style");
    style.id = "gm-global-style";
    style.textContent = `
      #gm-toast-box { position:fixed; right:16px; bottom:16px; z-index:2000; display:flex; flex-direction:column; gap:8px; }
      .gm-toast { background:rgba(24,24,24,0.92); color:#fff; padding:10px 12px; border-radius:8px; font:14px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial;
        box-shadow:0 6px 20px rgba(0,0,0,.25); opacity:1; transform:translateY(0); transition:opacity .8s ease, transform .8s ease; }
      .gm-toast.fade { opacity:0; transform:translateY(8px); }
      .gm-copy-btn { padding:6px 10px; border:1px solid #0b5cff; background:#0b5cff; color:#fff; border-radius:6px; cursor:pointer; }
      .gm-copy-btn:hover { filter:brightness(1.05); }
      .gm-del-btn { padding:6px 10px; border:1px solid #dc3545; background:#dc3545; color:#fff; border-radius:6px; cursor:pointer; margin-left:8px; }
      .gm-del-btn:hover { filter:brightness(1.05); }
      .selection-modal { position:fixed; inset:0; background:rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:1000; }
      .modal-overlay { background:#fff; padding:20px; border-radius:8px; width:300px; box-shadow:0 2px 10px rgba(0,0,0,.1); }
      .modal-content h3 { margin-top:0; color:#000; }
      .station-list { list-style:none; padding:0; margin:0; max-height:300px; overflow:auto; }
      .station-list li { cursor:pointer; padding:5px; border-bottom:1px solid #ccc; display:flex; color:#000; align-items:center; }
      .station-list li:hover { background:#f0f0f0; }
      .line-color { width:12px; height:12px; border-radius:50%; margin-right:8px; }
      .close-modal { margin-top:12px; }
    `;
    document.head.appendChild(style);
  }, []);

  const registerExclusiveToolStop = useCallback((toolName, stopFn) => {
    if (!toolName || typeof stopFn !== "function") return;
    exclusiveToolStopsRef.current.set(toolName, stopFn);
  }, []);

  const unregisterExclusiveToolStop = useCallback((toolName, stopFn) => {
    const current = exclusiveToolStopsRef.current.get(toolName);
    if (!current) return;

    if (!stopFn || current === stopFn) {
      exclusiveToolStopsRef.current.delete(toolName);
    }
  }, []);

  const activateExclusiveTool = useCallback((toolName) => {
    if (!toolName) return;

    const prevTool = activeExclusiveToolRef.current;

    if (prevTool && prevTool !== toolName) {
      const stopPrev = exclusiveToolStopsRef.current.get(prevTool);
      try {
        stopPrev?.();
      } catch (e) {
        console.warn(`Failed to stop exclusive tool "${prevTool}"`, e);
      }
    }

    activeExclusiveToolRef.current = toolName;
    setActiveExclusiveTool(toolName);
  }, []);

  const releaseExclusiveTool = useCallback((toolName) => {
    if (activeExclusiveToolRef.current === toolName) {
      activeExclusiveToolRef.current = null;
      setActiveExclusiveTool(null);
    }
  }, []);

  const isToolBlocked = useCallback(
    (toolName) => {
      return !!activeExclusiveTool && activeExclusiveTool !== toolName;
    },
    [activeExclusiveTool]
  );

  const showToast = (text) => {
    let box = document.getElementById("gm-toast-box");
    if (!box) {
      box = document.createElement("div");
      box.id = "gm-toast-box";
      document.body.appendChild(box);
    }
    const el = document.createElement("div");
    el.className = "gm-toast";
    el.textContent = text;
    box.appendChild(el);
    setTimeout(() => el.classList.add("fade"), 1200);
    setTimeout(() => {
      try {
        box.removeChild(el);
      } catch {}
    }, 2100);
  };

  const copyText = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      showToast("Координаты скопированы");
    } catch (e) {
      console.error(e);
      showToast("Не удалось скопировать");
    }
  };

  const getStationCoordsRaw = (element) =>
    element?.station_coordinate ?? element?.["Station coordinate"] ?? element?.stationCoordinate ?? null;

  const escapeHtml = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const formatDateRu = (dateStr) => {
    if (!dateStr) return "—";
    const s = String(dateStr);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}.${m[2]}.${m[1]}`;
  };

  const formatOckStatus = (status) => {
    const statusLabel = {
      created: "Создана",
      in_work: "В работе",
      ready: "Готово",
      done: "Выполнена",
    };
    return statusLabel[status] || status || "—";
  };

  const formatOckCapitalType = (capitalType) => {
    const capitalTypeLabel = {
      not_specified: "Не указана",
      capital: "Капитальная",
      non_capital: "Не капитальная",
    };
    return capitalTypeLabel[capitalType] || capitalType || "—";
  };

  // clone helpers
  const clearOckCloneTempMarks = () => {
    const map = mapRef.current;
    if (!map) return;

    for (const item of ockCloneTempRefs.current) {
      try {
        if (item?.placemark) map.geoObjects.remove(item.placemark);
      } catch {}
    }

    ockCloneTempRefs.current = [];
  };

  const stopOckClonePicking = () => {
    const map = mapRef.current;
    if (!map) return;

    for (const { event, fn } of ockCloneHandlersRef.current) {
      try {
        map.events.remove(event, fn);
      } catch {}
    }

    ockCloneHandlersRef.current = [];
  };

  const addOckCloneMapHandler = (map, event, fn) => {
    try {
      map.events.add(event, fn);
      ockCloneHandlersRef.current.push({ event, fn });
    } catch {}
  };

  const syncBalloonInventoryInput = (tempId, value) => {
    const input = document.getElementById(`gm-ock-temp-inv-${tempId}`);
    if (!input) return;

    const nextValue = value ?? "";
    if (input.value !== nextValue) {
      input.value = nextValue;
    }
  };

  const hasPointNearCoords = useCallback((lat, lon, points, excludeTempId = null) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;

    return (points || []).some((p) => {
      if (!p) return false;
      if (excludeTempId != null && Number(p.tempId) === Number(excludeTempId)) return false;

      const pLat = Number(p.lat);
      const pLon = Number(p.long);

      if (!Number.isFinite(pLat) || !Number.isFinite(pLon)) return false;

      const distance = getDistance(
        { latitude: lat, longitude: lon },
        { latitude: pLat, longitude: pLon }
      );

      return distance <= POINT_DUPLICATE_RADIUS_METERS;
    });
  }, []);

  const reverseGeocodeAddressOnMap = async (coords) => {
    try {
      const ymaps = ymapsApiRef.current || window.ymaps;
      if (!ymaps?.geocode) return "";
      const res = await ymaps.geocode(coords, { results: 1 });
      const first = res?.geoObjects?.get?.(0);
      if (!first) return "";

      const meta = first.properties?.get?.("metaDataProperty");
      const geocoderMeta = meta?.GeocoderMetaData || {};

      return (
        geocoderMeta?.text ||
        first.getAddressLine?.() ||
        first.properties?.get?.("text") ||
        first.properties?.get?.("name") ||
        ""
      );
    } catch (e) {
      console.warn("reverse geocode failed:", e);
      return "";
    }
  };

  const updateClonePointField = (tempId, patch) => {
    setOckCloneMode((prev) => {
      if (!prev.active) return prev;

      const next = {
        ...prev,
        points: (prev.points || []).map((p) =>
          Number(p.tempId) === Number(tempId) ? { ...p, ...patch } : p
        ),
      };

      ockCloneModeRef.current = next;
      return next;
    });
  };

  const getClonePointByTempId = (tempId) => {
    const snap = ockCloneModeRef.current;
    return (snap?.points || []).find((p) => Number(p.tempId) === Number(tempId)) || null;
  };

  const makeOckCloneEditorHtml = (tempId, point) => {
    const inventoryValue = escapeHtml(point?.inventory_number || "");

    return `
      <div style="min-width:260px">
        <div style="margin-bottom:8px;"><b>Новая точка ОСК</b></div>
        <div style="margin-bottom:8px; font-size:12px; color:#555;">
          ${escapeHtml(point?.address || "Адрес не определён")}
        </div>

        <label style="display:block; margin-bottom:6px; font-size:12px; color:#333;">
          Инвентарный номер
        </label>
        <input
          id="gm-ock-temp-inv-${tempId}"
          type="text"
          value="${inventoryValue}"
          placeholder="Введите инвентарный номер"
          style="
            width:100%;
            box-sizing:border-box;
            padding:8px 10px;
            border:1px solid #ccc;
            border-radius:6px;
            margin-bottom:10px;
          "
        />

        <div style="display:flex; gap:8px; flex-wrap:wrap;">
          <button id="gm-ock-temp-remove-${tempId}" class="gm-del-btn" style="margin-left:0">Удалить точку</button>
        </div>
      </div>
    `;
  };

  const refreshTempPlacemarkBalloon = (tempId, pointOverride = null) => {
    const tempItem = ockCloneTempRefs.current.find((x) => Number(x.tempId) === Number(tempId));
    const pm = tempItem?.placemark;
    const point = pointOverride || getClonePointByTempId(tempId);
    if (!pm || !point) return;

    try {
      pm.properties.set("balloonContentBody", makeOckCloneEditorHtml(tempId, point));
      pm.properties.set("balloonContentHint", point.address || `Новая точка ${tempId}`);

      // после обновления html заново привязываем input / remove
      setTimeout(() => {
        const input = document.getElementById(`gm-ock-temp-inv-${tempId}`);
        if (input && !input.__gmBound) {
          input.__gmBound = true;
          input.addEventListener("input", (e) => {
            updateClonePointField(tempId, {
              inventory_number: e.target.value,
            });
          });
        }

        const removeBtn = document.getElementById(`gm-ock-temp-remove-${tempId}`);
        if (removeBtn && !removeBtn.__gmBound) {
          removeBtn.__gmBound = true;
          removeBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            removeCloneTempPoint(tempId);
          });
        }
      }, 0);
    } catch {}
  };

  const handleClonePanelInventoryChange = (tempId, value) => {
    updateClonePointField(tempId, { inventory_number: value });
    syncBalloonInventoryInput(tempId, value);
  };

  const removeCloneTempPoint = (tempId) => {
    const map = mapRef.current;
    if (!map) return;

    const tempItem = ockCloneTempRefs.current.find((x) => Number(x.tempId) === Number(tempId));

    if (tempItem?.placemark) {
      try {
        tempItem.placemark.balloon?.close?.();
      } catch {}
      try {
        map.geoObjects.remove(tempItem.placemark);
      } catch {}
    }

    ockCloneTempRefs.current = ockCloneTempRefs.current.filter((x) => Number(x.tempId) !== Number(tempId));

    setOckCloneMode((prev) => {
      const next = {
        ...prev,
        points: (prev.points || []).filter((p) => Number(p.tempId) !== Number(tempId)),
      };
      ockCloneModeRef.current = next;
      return next;
    });

    showToast("Точка удалена");
  };

  const addOckCloneTempPlacemark = (pointData, colorKey = "created") => {
    const map = mapRef.current;
    const ymaps = ymapsApiRef.current || window.ymaps;
    if (!map || !ymaps?.Placemark) return;

    const { tempId, lat, long, address = "", inventory_number = "" } = pointData;

    const pm = new ymaps.Placemark(
      [lat, long],
      {
        balloonContentHeader: "Новая точка ОСК",
        balloonContentBody: makeOckCloneEditorHtml(tempId, {
          address,
          inventory_number,
        }),
        balloonContentHint: address || `Новая точка ${tempId}`,
      },
      {
        iconLayout: "default#image",
        iconImageHref: makeCircleSvgUrl(OCK_COLORS[colorKey]),
        iconImageSize: [22, 22],
        iconImageOffset: [-11, -11],
        draggable: true,
        zIndex: 30,
        openBalloonOnClick: !isMobile,
      }
    );

    pm.events.add("balloonopen", () => {
      const point = getClonePointByTempId(tempId);
      if (point) {
        refreshTempPlacemarkBalloon(tempId, point);
      }
    });

    pm.events.add("dragend", async () => {
      try {
        const coords = pm.geometry.getCoordinates();
        if (!Array.isArray(coords) || coords.length !== 2) return;

        const [newLat, newLon] = coords;

        const currentPoints = ockCloneModeRef.current?.points || [];
        if (hasPointNearCoords(newLat, newLon, currentPoints, tempId)) {
          const prevPoint = getClonePointByTempId(tempId);
          if (prevPoint) {
            pm.geometry.setCoordinates([prevPoint.lat, prevPoint.long]);
          }
          showToast("Нельзя поставить точку в то же место");
          return;
        }

        const newAddress = await reverseGeocodeAddressOnMap([newLat, newLon]);

        updateClonePointField(tempId, {
          lat: newLat,
          long: newLon,
          address: newAddress || "",
        });

        setTimeout(() => {
          refreshTempPlacemarkBalloon(tempId);
        }, 0);
      } catch (e) {
        console.warn("drag temp clone placemark failed:", e);
      }
    });

    try {
      map.geoObjects.add(pm);
      ockCloneTempRefs.current.push({ tempId, placemark: pm });
      if (!isMobile) {
        setTimeout(() => {
          try {
            pm.balloon.open();
          } catch {}
        }, 0);
      }
    } catch {}
  };

  const resetOckCloneMode = useCallback(() => {
    stopOckClonePicking();
    clearOckCloneTempMarks();

    const cleared = { active: false, sourcePointId: null, points: [] };
    ockCloneModeRef.current = cleared;
    setOckCloneMode(cleared);

    releaseExclusiveTool(EXCLUSIVE_TOOLS.OCK_CLONE);
  }, [releaseExclusiveTool]);

  const cancelOckCloneMode = useCallback(() => {
    resetOckCloneMode();
    showToast("Добавление сооружений отменено");
  }, [resetOckCloneMode]);

  const startOckCloneMode = (sourcePoint) => {
    if (!sourcePoint?.id || !mapRef.current) return;

    activateExclusiveTool(EXCLUSIVE_TOOLS.OCK_CLONE);
    stopOckClonePicking();
    clearOckCloneTempMarks();
    ockCloneTempIdRef.current = 1;

    const nextInit = {
      active: true,
      sourcePointId: sourcePoint.id,
      points: [],
    };

    ockCloneModeRef.current = nextInit;
    setOckCloneMode(nextInit);

    showToast("Режим добавления сооружений: кликайте по карте");

    const handler = async (e) => {
      const coords = e.get("coords");
      if (!Array.isArray(coords) || coords.length !== 2) return;

      const [lat, lon] = coords;

      const existingClonePoints = ockCloneModeRef.current?.points || [];
      if (hasPointNearCoords(lat, lon, existingClonePoints)) {
        showToast("Точка уже добавлена рядом");
        return;
      }

      const tempId = ockCloneTempIdRef.current++;
      const address = await reverseGeocodeAddressOnMap([lat, lon]);

      const newPoint = {
        tempId,
        lat,
        long: lon,
        address: address || "",
        inventory_number: "",
      };

      setOckCloneMode((prev) => {
        if (!prev.active) return prev;

        const next = {
          ...prev,
          points: [...prev.points, newPoint],
        };

        ockCloneModeRef.current = next;
        return next;
      });

      addOckCloneTempPlacemark(newPoint);
    };

    try {
      addOckCloneMapHandler(mapRef.current, "tap", handler);
      addOckCloneMapHandler(mapRef.current, "click", handler);
      addOckCloneMapHandler(mapRef.current, "touchstart", handler);
    } catch (e) {
      console.error(e);
    }
  };

  const focusCloneTempPoint = (tempId) => {
    const item = ockCloneTempRefs.current.find((x) => Number(x.tempId) === Number(tempId));
    const pm = item?.placemark;
    const map = mapRef.current;

    if (!pm || !map) return;

    try {
      const coords = pm.geometry.getCoordinates();
      map.setCenter(coords, 18, { duration: 250 });
      pm.balloon?.open?.();
    } catch (e) {
      console.warn("focusCloneTempPoint failed:", e);
    }
  };

  const saveOckCloneMode = async () => {
    const snap = ockCloneModeRef.current;
    const sourceId = snap?.sourcePointId;
    const selectedPoints = Array.isArray(snap?.points) ? snap.points : [];

    if (!sourceId) return;
    if (!selectedPoints.length) {
      showToast("Не выбраны точки");
      return;
    }

    const sourcePoint = (pointsOckCoord.details || []).find((x) => Number(x.id) === Number(sourceId));
    if (!sourcePoint) {
      showToast("Исходная точка не найдена");
      cancelOckCloneMode();
      return;
    }

    const tokenLocal = localStorage.getItem("auth_token");
    if (!tokenLocal) {
      alert("Нужна авторизация");
      return;
    }

    const archiveIds = Array.isArray(sourcePoint.archive)
      ? sourcePoint.archive.map((a) => Number(a?.id)).filter((n) => Number.isFinite(n))
      : [];

    const executors = Array.isArray(sourcePoint.executors)
      ? sourcePoint.executors.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      : [];

    const objectsOck = Array.isArray(sourcePoint.objects_ock)
      ? sourcePoint.objects_ock.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      : [];

    const stages = Array.isArray(sourcePoint.stages)
      ? sourcePoint.stages.map((id) => Number(id)).filter((n) => Number.isFinite(n))
      : [];

    let successCount = 0;
    const errors = [];

    setOckCloneSaving(true);
    try {
      for (const pt of selectedPoints) {
        const payload = {
          address: String(pt.address || "").trim(),
          lat: pt.lat,
          long: pt.long,

          status: "created",
          capital_type: sourcePoint.capital_type || "not_specified",
          needs_shurf: false,
          inventory_number: String(pt.inventory_number || "").trim(),

          is_visible: sourcePoint.is_visible !== false,
          description: sourcePoint.description || "",
          manager: sourcePoint.manager ?? null,

          object_ref: sourcePoint.object_ref ?? null,
          objects_ock: objectsOck,
          main_stage: sourcePoint.main_stage ?? null,
          stages: stages,

          executors: executors,
          archive_ids: archiveIds,
        };

        const res = await fetch(`${URL}/api/points-ock/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${tokenLocal}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          errors.push({
            point: pt,
            error: typeof data === "object" ? JSON.stringify(data) : `HTTP ${res.status}`,
          });
          continue;
        }

        successCount += 1;
      }

      if (successCount > 0) {
        showToast(`Сооружения успешно добавлены: ${successCount}`);
        fetchData("points-ock", setPointsOckCoord);
      } else {
        showToast("Не удалось добавить сооружения");
      }

      if (errors.length) {
        console.error("OCK clone create errors:", errors);
        alert(`Создано: ${successCount}. Ошибок: ${errors.length}. Подробности в консоли.`);
      }
    } catch (e) {
      console.error("saveOckCloneMode failed:", e);
      alert("Ошибка при сохранении сооружений");
    } finally {
      setOckCloneSaving(false);
      resetOckCloneMode();
    }
  };

  useEffect(() => {
    registerExclusiveToolStop(EXCLUSIVE_TOOLS.OCK_CLONE, resetOckCloneMode);

    return () => {
      unregisterExclusiveToolStop(EXCLUSIVE_TOOLS.OCK_CLONE, resetOckCloneMode);
    };
  }, [registerExclusiveToolStop, unregisterExclusiveToolStop, resetOckCloneMode]);

  const makeCopyBtnHtml = (id) => `<button id="${id}" class="gm-copy-btn">Копировать координаты</button>`;
  const makeDeleteBtnHtml = (id) => `<button id="${id}" class="gm-del-btn">Удалить</button>`;

  const makeOckCloneButtonsHtml = (pointId) => {
    return `
      <div style="margin-top:8px; display:inline-block">
        <button id="gm-ock-clone-start-${pointId}" class="gm-copy-btn">Добавить сооружение</button>
      </div>
    `;
  };

  const authorHtml = (nameOrEmail) =>
    `<div style="margin-top:6px;font-size:12px;color:#666">Автор: ${nameOrEmail || "не указан"}</div>`;

  const buildAuthorBlock = (element) => {
    const aId = element?.author_id ?? element?.author?.id ?? null;
    const aName = element?.author_username ?? element?.author?.username ?? element?.author_email ?? "";
    const isOwner = aId && userId && Number(aId) === Number(userId);
    const canDelete = isAdmin || isOwner;
    const authorVisible = isAdmin || isOwner;
    const authorBlock = authorVisible ? authorHtml(aName) : "";
    return { isOwner, canDelete, authorBlock };
  };


  const getOckObjectTitle = (p) =>
    p?.object_ref_name ||
    p?.object_ref_display ||
    p?.object_ref_detail?.name ||
    p?.object_ref?.name ||
    p?.object_name ||
    (typeof p?.object_ref === "number" ? `ID ${p.object_ref}` : null) ||
    "—";

  const getOckStageTitle = (p) => {
    const stageName =
      p?.main_stage_name ||
      p?.main_stage_display ||
      p?.main_stage_detail?.name ||
      p?.main_stage?.name ||
      p?.stage_name ||
      (typeof p?.main_stage === "number" ? `ID ${p.main_stage}` : null);

    const stageType =
      p?.main_stage_type_display ||
      p?.main_stage_detail?.stage_type_display ||
      p?.main_stage?.stage_type_display ||
      p?.main_stage?.stage_type;

    if (stageType && stageName && !String(stageName).startsWith("ID ")) {
      return `${stageType} — ${stageName}`;
    }
    return stageName || "—";
  };

  const getOckManagerTitle = (p) => {
    const direct =
      p?.manager_username || p?.manager_detail?.username || p?.manager_detail?.full_name || p?.manager_name;

    if (direct && String(direct).trim()) return String(direct).trim();

    const m = p?.manager_detail || p?.manager_obj || p?.manager;
    if (m && typeof m === "object") {
      const fullName =
        [m.last_name, m.first_name].filter(Boolean).join(" ").trim() ||
        m.full_name ||
        m.username ||
        m.email;
      if (fullName) return fullName;
    }

    if (typeof p?.manager === "number") return `ID ${p.manager}`;
    return "—";
  };

  const getOckExecutorsTitle = (p) => {
    const details = Array.isArray(p?.executors_detail) ? p.executors_detail : [];

    if (details.length) {
      const names = details
        .map((u) => {
          if (!u) return "";
          const fullName =
            String(u.full_name || "").trim() ||
            [u.last_name, u.first_name].filter(Boolean).join(" ").trim() ||
            String(u.username || "").trim() ||
            (typeof u.id === "number" ? `ID ${u.id}` : "");
          return fullName;
        })
        .filter(Boolean);

      return names.length ? names.join(", ") : "—";
    }

    if (Array.isArray(p?.executors) && p.executors.length) {
      return p.executors.map((id) => (typeof id === "number" ? `ID ${id}` : String(id))).join(", ");
    }

    return "—";
  };

  const bindBalloonBtn = (ref, btnId, handler, tries = 20) => {
    if (!ref?.balloon?.getOverlay) return;

    ref.balloon
      .getOverlay()
      .then((overlay) => {
        const root = overlay?.getElement?.();
        if (!root) {
          if (tries > 0) requestAnimationFrame(() => bindBalloonBtn(ref, btnId, handler, tries - 1));
          return;
        }

        const btn = root.querySelector(`#${btnId}`);
        if (!btn) {
          if (tries > 0) requestAnimationFrame(() => bindBalloonBtn(ref, btnId, handler, tries - 1));
          return;
        }

        if (btn.__gmBound) return;
        btn.__gmBound = true;

        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          handler?.(e);
        });
      })
      .catch(() => {
        if (tries > 0) requestAnimationFrame(() => bindBalloonBtn(ref, btnId, handler, tries - 1));
      });
  };

  const attachCopyButton = (btnId, getCoords, isPolygon = false) => {
    balloonActionsRef.current.set(btnId, () => {
      const raw = getCoords?.();
      if (!raw || (Array.isArray(raw) && raw.length === 0)) {
        showToast("Нет координат");
        return;
      }
      copyText(formatMggtForCopy(raw, { isPolygon }));
    });
  };

  const attachDeleteButton = (btnId, onDelete) => {
    balloonActionsRef.current.set(btnId, () => onDelete?.());
  };

  const registerOckCloneActions = (pointId, pointData) => {
    balloonActionsRef.current.set(`gm-ock-clone-start-${pointId}`, () => {
      if (activeExclusiveToolRef.current && activeExclusiveToolRef.current !== "ock-clone") {
        showToast("Сначала завершите текущее действие");
        return;
      }
  
      startOckCloneMode(pointData);
      try {
        const ref = layerRefs.current.ockpoints?.[String(pointId)];
        ref?.balloon?.close?.();
      } catch {}
    });
  };

  const postEntity = async ({ name, line, coords, isPolygone, mode }) => {
    const isStation = mode === "stations";
    const endpoint = isStation ? "stations" : "linesections";
    const body = isStation
      ? { name, line, lat: coords[0][0], long: coords[0][1], station_coordinate: coords }
      : { name, line, coordinate: coords, isPolygone: !!isPolygone };

    try {
      await fetch(`${URL}/api/${endpoint}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Token ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      fetchData(endpoint, isStation ? setCoordinates : setLinecoordinates);
    } catch (err) {
      console.error(`Не удалось сохранить данные в ${endpoint}`, err);
      alert("Ошибка сохранения (см. консоль).");
    }
  };

  const deleteEntity = async (endpoint, id) => {
    if (!token) {
      alert("Нужна авторизация");
      return;
    }
    if (!window.confirm("Удалить объект?")) return;
    try {
      const res = await fetch(`${URL}/api/${endpoint}/${id}/`, {
        method: "DELETE",
        headers: { Authorization: `Token ${token}` },
      });
      if (res.status === 204 || res.ok) {
        showToast("Удалено");
        fetchData(endpoint, endpoint === "stations" ? setCoordinates : setLinecoordinates);
      } else {
        const txt = await res.text();
        alert(`Не удалось удалить: ${res.status} ${txt}`);
      }
    } catch (e) {
      console.error("delete failed", e);
      alert("Ошибка удаления (см. консоль).");
    }
  };

  const fetchData = (endpoint, setter) => {
    const tokenLocal = localStorage.getItem("auth_token");
    const headers = tokenLocal ? { Authorization: `Token ${tokenLocal}` } : {};
    return fetch(`${URL}/api/${endpoint}/`, { method: "GET", headers })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((data) => setter({ details: data }))
      .catch((e) => {
        if (endpoint === "buildings" && String(e.message) === "403") {
          setter({ details: [] });
          return;
        }
        console.error(`Error fetching ${endpoint}:`, e);
      });
  };

  useEffect(() => {
    if (canUseNavigation) {
      fetchData("crossway", setCrossway);
      fetchData("stations", setCoordinates);
      fetchData("linesections", setLinecoordinates);
      fetchData("hermodoors", setHermodoorcoord);
      fetchData("wayouts", setWayoutscoord);
      fetchData("metrobridges", setMetromostcoord);
      fetchData("depos", setDepocoord);
    } else {
      setLinecoordinates({ details: [] });
      setHermodoorcoord({ details: [] });
      setCrossway({ details: [] });
      setWayoutscoord({ details: [] });
      setDepocoord({ details: [] });
      setMetromostcoord({ details: [] });
      setCoordinates({ details: [] });
    }
  }, [canUseNavigation]);

  useEffect(() => {
    if (canViewBuildings) fetchData("buildings", setBuildingscoord);
    else setBuildingscoord({ details: [] });
  }, [canViewBuildings]);

  useEffect(() => {
    if (mainOck || canViewOck) {
      fetchData("points-ock", setPointsOckCoord);
    } else {
      setPointsOckCoord({ details: [] });
    }
  }, [mainOck, canViewOck]);

  useEffect(() => {
    if (canViewArchiveBuildings) {
      fetchData("archive-buildings", setArchiveBuildingsCoord);
    } else {
      setArchiveBuildingsCoord({ details: [] });
    }
  }, [canViewArchiveBuildings]);

  useEffect(() => {
    return () => {
      try {
        resetOckCloneMode();
      } catch {}
    };
  }, [resetOckCloneMode]);

  const toggleActiveStation = (stationId) => {
    if (stationId == null) return;

    setActiveIds((prev) => {
      const id = Number(stationId);
      const prevStations = Array.isArray(prev.activeStations) ? prev.activeStations : [];
      const isOn = prevStations.includes(id);

      if (!isOn) {
        setActiveStationId(id);
        return { ...prev, activeStations: [...prevStations, id] };
      }

      const nextStations = prevStations.filter((x) => x !== id);

      setActiveStationId((last) => {
        if (Number(last) !== id) return last;
        return nextStations.length ? nextStations[nextStations.length - 1] : null;
      });

      return { ...prev, activeStations: nextStations };
    });
  };

  const selectOrFocusStation = (stationId) => {
    if (stationId == null) return;
    const id = Number(stationId);

    setActiveIds((prev) => {
      const prevStations = Array.isArray(prev.activeStations) ? prev.activeStations : [];
      return prevStations.includes(id) ? prev : { ...prev, activeStations: [...prevStations, id] };
    });

    setActiveStationId(id);
  };

  const toggleActiveLine = (lineIds) => {
    setActiveIds((prev) => {
      const updatedActiveLines = [...prev.activeLines];
      lineIds.forEach((lineId) => {
        const isActive = updatedActiveLines.includes(lineId);
        if (isActive) {
          const i = updatedActiveLines.indexOf(lineId);
          if (i !== -1) updatedActiveLines.splice(i, 1);
        } else {
          updatedActiveLines.push(lineId);
        }
      });
      return { ...prev, activeLines: updatedActiveLines };
    });
  };

  useEffect(() => {
    const prevId = lastBalloonIdRef.current;

    if (prevId != null && polygonsRef.current[prevId]) {
      try {
        polygonsRef.current[prevId].balloon.close();
      } catch {}
    }

    if (activeStationId != null && polygonsRef.current[activeStationId]) {
      try {
        polygonsRef.current[activeStationId].balloon.open();
      } catch {}
      lastBalloonIdRef.current = activeStationId;
    } else {
      lastBalloonIdRef.current = null;
    }
  }, [activeStationId]);

  useEffect(() => {
    if (!mapRef.current) return;
    const ids = activeIds.activeLines;
    if (!ids || ids.length === 0) return;

    let minLat = 90;
    let minLon = 180;
    let maxLat = -90;
    let maxLon = -180;
    const idSet = new Set(ids);

    linecoordinates.details.forEach((el) => {
      if (!idSet.has(el.id)) return;
      const coord = el.coordinate;
      const pushPoint = (p) => {
        const [lat, lon] = p;
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
        }
      };
      if (Array.isArray(coord?.[0]?.[0])) coord[0].forEach(pushPoint);
      else if (Array.isArray(coord)) coord.forEach(pushPoint);
    });

    if (minLat <= maxLat && minLon <= maxLon && Number.isFinite(minLat)) {
      try {
        mapRef.current.setBounds(
          [
            [minLat, minLon],
            [maxLat, maxLon],
          ],
          { checkZoomRange: true, zoomMargin: 50 }
        );
      } catch (e) {
        const firstId = ids[0];
        const el = linecoordinates.details.find((x) => x.id === firstId);
        const p = Array.isArray(el?.coordinate?.[0]) ? el.coordinate[0] : null;
        if (p) mapRef.current.setCenter(p, 14);
      }
    }
  }, [activeIds.activeLines, linecoordinates.details]);

  const hasDocs = (el) => {
    const d = el?.documentation ?? el?.docs ?? el?.documents;
    const hasDocField =
      (Array.isArray(d) && d.length > 0) ||
      (typeof d === "string" && d.trim().length > 0) ||
      (!!d && typeof d === "object");

    return hasDocField || getArchiveLen(el) > 0;
  };

  const DOC_KEYS = new Set(["docs", "documentation", "docsOnly", "withDocs"]);
  const docsOnly =
    visible.includes("docs") ||
    visible.includes("documentation") ||
    visible.includes("docsOnly") ||
    visible.includes("withDocs");

  const getArchiveLen = (el) => {
    const a = el?.archive;
    if (Array.isArray(a)) return a.length;
    if (!a) return 0;
    if (typeof a === "string") return a.trim() ? 1 : 0;
    if (typeof a?.length === "number") return a.length;
    if (typeof a === "object") return Object.keys(a).length;
    return 0;
  };

  const [newCoords, setNewCoords] = useState(null);
  const [closestCoords, setClosestCoords] = useState(null);
  const [nearestLines, setNearestLines] = useState([]);

  const iterLinePoints = (coord) => {
    if (!coord) return [];
    if (Array.isArray(coord?.[0]?.[0])) return coord[0];
    if (Array.isArray(coord?.[0]) && coord[0].length === 2) return coord;
    return [];
  };

  function calculateDistances(coords) {
    const userLat = parseFloat(coords[0]);
    const userLon = parseFloat(coords[1]);
    if (!Number.isFinite(userLat) || !Number.isFinite(userLon)) return;

    const radiusKm = 5;
    const kmToLat = radiusKm / 111;
    const kmToLon = radiusKm / (111 * Math.cos((userLat * Math.PI) / 180));

    const latMin = userLat - kmToLat;
    const latMax = userLat + kmToLat;
    const lonMin = userLon - kmToLon;
    const lonMax = userLon + kmToLon;

    const results = (linecoordinates.details || []).map((line) => {
      let minDistance = Infinity;
      let closest_coords = null;

      const pts = iterLinePoints(line.coordinate);

      pts.forEach((point) => {
        const [pointLat, pointLon] = point || [];
        if (!Number.isFinite(pointLat) || !Number.isFinite(pointLon)) return;

        if (pointLat >= latMin && pointLat <= latMax && pointLon >= lonMin && pointLon <= lonMax) {
          const distance = getDistance(
            { latitude: userLat, longitude: userLon },
            { latitude: pointLat, longitude: pointLon }
          );
          if (distance < minDistance) {
            minDistance = distance;
            closest_coords = [pointLat, pointLon];
          }
        }
      });

      return {
        line_id: line.id,
        name: line.name,
        min_distance: Number.isFinite(minDistance) ? minDistance / 1000 : Infinity,
        closest_coords,
        line: line.line,
      };
    });

    const nearbyLines = results.filter((line) => line.min_distance <= radiusKm && line.closest_coords);
    const uniqueNearbyLines = Object.values(
      nearbyLines.reduce((acc, line) => {
        if (!acc[line.line] || acc[line.line].min_distance > line.min_distance) acc[line.line] = line;
        return acc;
      }, {})
    );

    setNearestLines(uniqueNearbyLines);
  }

  const [yandexPoint, setYandexPoint] = useState(null);

  const onYandexPoint = (payload) => {
    if (!payload) {
      setYandexPoint(null);
      return;
    }
    setYandexPoint(payload);
  };

  function handlePointClick(point) {
    const targetPolygon = polygonsRef.current[point.id];
    if (!targetPolygon) {
      console.error("Не удалось найти полигон для точки:", point.id);
      return;
    }
    selectOrFocusStation(point.id);
  }

  const layerRefs = useRef({
    lines: {},
    stations: {},
    crossways: {},
    depo: {},
    metrobridge: {},
    wayouts: {},
    hermodoors: {},
    ockpoints: {},
    buildings: {},
  });

  const layerMeta = useRef({
    lines: {},
    stations: {},
    crossways: {},
    depo: {},
    metrobridge: {},
    wayouts: {},
    hermodoors: {},
    ockpoints: {},
    buildings: {},
  });

  const visRafRef = useRef(0);

  const scheduleApplyVisibility = () => {
    if (visRafRef.current) return;
    visRafRef.current = requestAnimationFrame(() => {
      visRafRef.current = 0;
      try {
        applyVisibilityToAll();
      } catch {}
    });
  };

  const setRef = (group, id, ref, meta) => {
    if (!id) return;
    if (ref) layerRefs.current[group][id] = ref;
    if (meta) layerMeta.current[group][id] = meta;
    scheduleApplyVisibility();
  };

  const applyVisibilityToGroup = (group, isVisible) => {
    const refs = layerRefs.current[group];
    Object.values(refs).forEach((ref) => {
      if (!ref) return;
      try {
        ref.options.set("visible", !!isVisible);
      } catch {
        try {
          ref.options.set("opacity", isVisible ? 1 : 0);
          ref.options.set("strokeOpacity", isVisible ? 1 : 0);
          ref.options.set("fillOpacity", isVisible ? 0.6 : 0);
        } catch {}
      }
    });
  };

  const rebuildBuildingsCluster = () => {
    const cl = buildingsClustererRef.current;
    if (!cl) return;

    const refs = layerRefs.current.buildings || {};
    const visibleObjs = Object.values(refs).filter((r) => {
      try {
        return r?.options?.get?.("visible") !== false;
      } catch {
        return true;
      }
    });

    try {
      cl.removeAll();
    } catch {}
    if (visibleObjs.length === 0) {
      try {
        cl.options.set("visible", false);
      } catch {}
      return;
    }

    try {
      cl.options.set("visible", true);
    } catch {}
    try {
      cl.add(visibleObjs);
    } catch {}
  };

  const applyVisibilityToAll = () => {
    const groups = Object.keys(layerRefs.current);

    for (const group of groups) {
      const refs = layerRefs.current[group];

      if (group === "buildings" && !canViewBuildings) {
        applyVisibilityToGroup(group, false);
        continue;
      }

      Object.entries(refs).forEach(([id, ref]) => {
        if (!ref) return;

        const meta = layerMeta.current[group]?.[id] || {};
        const archiveLen = meta.archiveLen ?? 0;
        const elHasDocs = !!meta.hasDocs;

        const wantsArchive = visible.includes("archive");
        const selectedKeys = visible.filter((v) => v !== "archive" && !DOC_KEYS.has(v));
        const hasLayerSelection = selectedKeys.length > 0;
        const inSelectedLayer = hasLayerSelection ? selectedKeys.includes(group) : true;
        const wantsDocs = docsOnly;
        const passArchive = wantsArchive ? (archiveLen ?? 0) !== 0 : true;

        const passDocsUnion = wantsDocs
          ? hasLayerSelection
            ? inSelectedLayer || elHasDocs
            : elHasDocs
          : inSelectedLayer;

        const ok = passArchive && passDocsUnion;

        try {
          ref.options.set("visible", !!ok);
        } catch {
          try {
            ref.options.set("opacity", ok ? 1 : 0);
            ref.options.set("strokeOpacity", ok ? 1 : 0);
            ref.options.set("fillOpacity", ok ? 0.6 : 0);
          } catch {}
        }
      });
    }

    rebuildBuildingsCluster();
  };

  useEffect(() => {
    scheduleApplyVisibility();
  }, [visible, docsOnly, canViewBuildings]);

  const station_sq = useMemo(() => {
    return (coordinates.details || []).map((element) => {
      const key = element.id ?? `${element.name}-st`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-st-${stableId}`;
      const delBtnId = `gm-del-st-${element.id}`;
      const raw = getStationCoordsRaw(element);
      if (!raw || !Array.isArray(raw) || raw.length === 0) return null;
      const geom = Array.isArray(raw?.[0]?.[0]) ? raw : [raw];

      const { canDelete, authorBlock } = buildAuthorBlock(element);
      const footerHtml = authorBlock + makeCopyBtnHtml(btnId) + (canDelete ? makeDeleteBtnHtml(delBtnId) : "");

      const isSelected = activeIds.activeStations.includes(element.id);
      const isLast = Number(activeStationId) === Number(element.id);

      let stationOpacity = 1.0;
      if (activeIds.activeLines.length > 0) {
        const lineIds = new Set(activeIds.activeLines);
        const stationLineIds = linecoordinates.details.filter((l) => l.line === element.line).map((l) => l.id);
        const intersects = stationLineIds.some((id) => lineIds.has(id));
        stationOpacity = intersects ? 1.0 : 0.35;
      }

      if (isSelected || isLast) stationOpacity = 1.0;

      return (
        <Polygon
          key={key}
          modules={["geoObject.addon.balloon"]}
          geometry={geom}
          options={{
            fillColor: "#5e5b5b",
            strokeColor: "#2b2929",
            opacity: stationOpacity,
            strokeWidth: 5,
            zIndex: isLast ? 10 : isSelected ? 8 : 6,
          }}
          onClick={() => handlePointClick(element)}
          instanceRef={(ref) => {
            if (ref) {
              polygonsRef.current[element.id] = ref;
              attachCopyButton(btnId, () => getStationCoordsRaw(element), true);
              if (canDelete) attachDeleteButton(delBtnId, () => deleteEntity("stations", element.id));

              setRef("stations", String(element.id), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            stationId: element.id,
            balloonContentHeader: `<a href='stations/${element.id}'>${element.name}</a>`,
            balloonContentBody: `${element.line ?? ""}`,
            balloonContentFooter: footerHtml,
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coordinates.details,
    linecoordinates.details,
    activeIds.activeLines,
    activeIds.activeStations,
    activeStationId,
    userId,
    isAdmin,
  ]);

  const depo = useMemo(() => {
    return (depocoord.details || []).map((element) => {
      if (!element?.coordinate) return null;

      const key = element.id ?? `depo-${element.name}`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-depo-${stableId}`;

      return (
        <Polygon
          key={key}
          geometry={[element.coordinate]}
          options={{
            fillColor: "#5e5b5b",
            strokeColor: (element?.archive?.length ?? 0) > 0 ? "#ffffff" : "#2b2929",
            opacity: 1,
            strokeWidth: 5,
            zIndex: 4,
          }}
          instanceRef={(ref) => {
            if (ref) {
              polygonsRef.current[element.id] = ref;
              attachCopyButton(btnId, () => element.coordinate, true);

              setRef("depo", String(element.id), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            balloonContentHeader: `${element.name}`,
            balloonContentFooter: makeCopyBtnHtml(btnId),
          }}
        />
      );
    });
  }, [depocoord.details]);

  const metrobridge = useMemo(() => {
    return (metromostcoord.details || []).map((element) => {
      if (!element?.coordinate) return null;

      const key = element.id ?? `bridge-${element.name}`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-bridge-${stableId}`;

      return (
        <Polygon
          key={key}
          geometry={[element.coordinate]}
          options={{
            fillColor: "#5e5b5b",
            strokeColor: (element?.archive?.length ?? 0) > 0 ? "#ffffff" : "#2b2929",
            opacity: 1,
            strokeWidth: 5,
            zIndex: 3,
          }}
          instanceRef={(ref) => {
            if (ref) {
              polygonsRef.current[element.id] = ref;
              attachCopyButton(btnId, () => element.coordinate, true);

              setRef("metrobridge", String(element.id), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            balloonContentHeader: `${element.name}`,
            balloonContentFooter: makeCopyBtnHtml(btnId),
          }}
        />
      );
    });
  }, [metromostcoord.details]);

  const linesection_sq = useMemo(() => {
    return (linecoordinates.details || []).map((element) => {
      if (!element?.coordinate) return null;

      const key = element.id ?? `${element.name}-ln`;
      const isPoly = !!element?.isPolygone;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-line-${stableId}`;
      const delBtnId = `gm-del-line-${element.id}`;
      const { canDelete, authorBlock } = buildAuthorBlock(element);

      const alphaBase = isDLineName(element.line) ? 0.6 : 0.8;

      let isSelectedByActive = true;
      if (activeIds.activeLines.length > 0) isSelectedByActive = activeIds.activeLines.includes(element.id);

      const objOpacity = isSelectedByActive ? 0.8 : 0.15;
      const strokeOpacity = isSelectedByActive ? alphaBase : 0.3;
      const fillOpacity = isSelectedByActive ? alphaBase : 0.25;

      const color = `#${metroLineColors[element.line] ?? "000000"}`;

      const buttonsHtml = authorBlock + makeCopyBtnHtml(btnId) + (canDelete ? makeDeleteBtnHtml(delBtnId) : "");

      const commonProps = {
        properties: {
          balloonContentHeader: `<a href='linesections/${element.id}'>${element.name}</a>`,
          balloonContentBody: `${element.line ?? ""}`,
          balloonContentFooter: buttonsHtml,
        },
        onClick: () => {
          const ref = linesRef.current[element.id];
          if (ref?.balloon) ref.balloon.open();
        },
      };

      return isPoly ? (
        <Polygon
          key={key}
          modules={["geoObject.addon.balloon"]}
          geometry={[element.coordinate]}
          options={{
            opacity: objOpacity,
            fillColor: color,
            strokeColor: color,
            fillOpacity,
            strokeOpacity,
            strokeWidth: 8,
            zIndex: 1,
            cursor: "pointer",
          }}
          instanceRef={(ref) => {
            if (ref) {
              linePolyRefs.current[element.id] = ref;
              attachCopyButton(btnId, () => element.coordinate, isPoly);
              if (canDelete) attachDeleteButton(delBtnId, () => deleteEntity("linesections", element.id));
              linesRef.current[element.id] = ref;

              setRef("lines", String(element.id), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          {...commonProps}
        />
      ) : (
        <Polyline
          key={key}
          geometry={element.coordinate}
          options={{
            opacity: objOpacity,
            strokeColor: color,
            strokeOpacity,
            strokeWidth: 8,
            zIndex: 1,
            cursor: "pointer",
          }}
          instanceRef={(ref) => {
            if (ref) {
              linesRef.current[element.id] = ref;
              attachCopyButton(btnId, () => element.coordinate, false);
              if (canDelete) attachDeleteButton(delBtnId, () => deleteEntity("linesections", element.id));

              setRef("lines", String(element.id), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          {...commonProps}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linecoordinates.details, activeIds.activeLines, userId, isAdmin]);

  const wayouts = useMemo(() => {
    return (wayoutscoord.details || []).map((element, index) => {
      if (!element?.coordinate) return null;

      const key = element.id ?? `wayout-${index}`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-wayout-${stableId}`;

      const hasSelection = activeIds.activeLines.length > 0 || activeIds.activeStations.length > 0;
      const opacity = hasSelection ? 0.3 : 0.8;

      return element.isPolygone ? (
        <Polygon
          key={key}
          geometry={[element.coordinate]}
          options={{
            strokeColor: "#231F20",
            opacity,
            strokeWidth: 8,
            zIndex: 2,
          }}
          onClick={() => handlePointClick(element)}
          instanceRef={(ref) => {
            if (ref) {
              attachCopyButton(btnId, () => element.coordinate, true);

              setRef("wayouts", String(element.id ?? key), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            balloonContentHeader: "Съезд",
            balloonContentFooter: makeCopyBtnHtml(btnId),
          }}
        />
      ) : (
        <Polyline
          key={key}
          geometry={element.coordinate}
          options={{
            strokeColor: "#231F20",
            opacity,
            strokeWidth: 8,
            strokeOpacity: hasSelection ? 0.1 : 1,
          }}
          onClick={() => handlePointClick(element)}
          instanceRef={(ref) => {
            if (ref) {
              attachCopyButton(btnId, () => element.coordinate, false);

              setRef("wayouts", String(element.id ?? key), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            balloonContentHeader: "Съезд",
            balloonContentFooter: makeCopyBtnHtml(btnId),
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wayoutscoord.details, activeIds.activeLines, activeIds.activeStations]);

  const crossways = useMemo(() => {
    return (crossway.details || []).map((element, index) => {
      if (!element?.crossway_coordinate) return null;

      const key = element.id ?? `cross-${index}`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-cross-${stableId}`;

      const hasSelection = activeIds.activeLines.length > 0 || activeIds.activeStations.length > 0;
      const opacity = hasSelection ? 0.3 : 0.8;

      return (
        <Polygon
          key={key}
          geometry={[element.crossway_coordinate]}
          options={{
            fillColor: "#808080",
            strokeColor: "#000000",
            opacity,
            strokeWidth: 3,
            zIndex: 5,
          }}
          instanceRef={(ref) => {
            if (ref) {
              attachCopyButton(btnId, () => element.crossway_coordinate, true);

              setRef("crossways", String(element.id ?? key), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
          properties={{
            balloonContentHeader: `${element.id}`,
            balloonContentFooter: makeCopyBtnHtml(btnId),
          }}
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossway.details, activeIds.activeLines, activeIds.activeStations]);

  const hermodors = useMemo(() => {
    return (hermodoorcoord.details || []).map((element, index) => {
      if (typeof element?.lat !== "number" || typeof element?.long !== "number") return null;

      const key = element.id ?? `hermo-${index}`;
      const stableId = element.id;
      if (!stableId) return null;
      const btnId = `gm-copy-hermo-${stableId}`;

      return (
        <Placemark
          key={key}
          geometry={[element.lat, element.long]}
          properties={{
            balloonContentHeader: `<a href='hermodoors/${element.id}'>${element.name}</a>`,
            balloonContentBody: element.line ?? "",
            balloonContentFooter: makeCopyBtnHtml(btnId),
            balloonContentHint: element.name,
          }}
          options={{
            draggable: false,
            iconLayout: "default#image",
            iconImageSize: [20, 20],
            iconImageHref: door,
            iconImageOffset: [-10, -20],
            opacity: 1,
          }}
          instanceRef={(ref) => {
            if (ref) {
              placemarksRef.current[element.id] = ref;
              attachCopyButton(btnId, () => [[element.lat, element.long]], false);

              setRef("hermodoors", String(element.id ?? key), ref, {
                hasDocs: hasDocs(element),
                archiveLen: getArchiveLen(element),
              });
            }
          }}
        />
      );
    });
  }, [hermodoorcoord.details]);

  const ockGroups = useMemo(() => {
    const groups = {
      created: [],
      in_work: [],
      ready: [],
      done: [],
      hidden: [],
    };

    for (const p of ockPointsForRender) {
      const key = getOckPointColorKey(p, strictOckAccess);
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }

    return groups;
  }, [ockPointsForRender, strictOckAccess]);

  const pointsOck = useMemo(() => {
    const clustererOptionsBase = {
      gridSize: 60,
      clusterDisableClickZoom: true,
      clusterOpenBalloonOnClick: false,
    };

    const order = ["created", "in_work", "ready", "done", "hidden"];

    const renderPlacemark = (p, colorKey, markerOpacity = 1) => {
      if (typeof p?.lat !== "number" || typeof p?.long !== "number") return null;

      const key = `${p.id}-${colorKey}-${markerOpacity === 1 ? "bright" : "faded"}`;
      const stableId = p.id;
      if (!stableId) return null;

      const btnId = `gm-copy-ock-${stableId}`;

      const objectTitle = getOckObjectTitle(p);
      const stageTitle = getOckStageTitle(p);
      const managerTitle = getOckManagerTitle(p);
      const executorsTitle = getOckExecutorsTitle(p);
      const inventoryNumber = p?.inventory_number || "—";
      const plannedFinishDate = formatDateRu(p?.planned_finish_date);

      const isCloneActiveForThisPoint =
        ockCloneMode.active && Number(ockCloneMode.sourcePointId) === Number(p.id);

      return (
        <Placemark
          key={key}
          geometry={[p.lat, p.long]}
          properties={{
            gmOckId: Number(p.id),
            balloonContentHeader: `<a href='ock-points/${p.id}'>Точка ОСК №${p.id}</a>`,
            balloonContentBody: `
              <div><b>Адрес:</b> ${escapeHtml(p.address || "—")}</div>
              <div><b>Инвентарный номер:</b> ${escapeHtml(inventoryNumber)}</div>
              <div><b>Объект:</b> ${escapeHtml(objectTitle)}</div>
              <div><b>Этап:</b> ${escapeHtml(stageTitle)}</div>
              <div><b>Статус:</b> ${escapeHtml(formatOckStatus(p.status))}</div>
              <div><b>Капитальность:</b> ${escapeHtml(formatOckCapitalType(p.capital_type))}</div>
              <div><b>Шурф:</b> ${p.needs_shurf ? "Да" : "Нет"}</div>
              <div><b>Руководитель:</b> ${escapeHtml(managerTitle)}</div>
              <div><b>Исполнитель(-и):</b> ${escapeHtml(executorsTitle)}</div>
              <div><b>Срок завершения работы:</b> ${escapeHtml(plannedFinishDate)}</div>
            `,
            balloonContentFooter:
              makeCopyBtnHtml(btnId) +
              (canCloneOck ? makeOckCloneButtonsHtml(p.id, isCloneActiveForThisPoint) : ""),
            balloonContentHint: p.address || `ОСК ${p.id ?? ""}`,
          }}
          options={{
            iconLayout: "default#image",
            iconImageHref: makeCircleSvgUrl(OCK_COLORS[colorKey], markerOpacity, 22),
            iconImageSize: [22, 22],
            iconImageOffset: [-11, -11],
            draggable: false,
            zIndex: 30,
          }}
          instanceRef={(ref) => {
            if (ref) {
              attachCopyButton(btnId, () => [[p.lat, p.long]], false);

              if (canManageOck && !isGuest) {
                registerOckCloneActions(p.id, p);
              }

              setRef("ockpoints", String(p.id ?? key), ref, {
                hasDocs: hasDocs(p),
                archiveLen: getArchiveLen(p),
              });
            }
          }}
          onClick={() => {
            const ref = layerRefs.current.ockpoints?.[String(p.id)];
            if (!ref?.balloon) return;

            if (isClusterZoomingRef.current) {
              setTimeout(() => {
                try {
                  ref.balloon.open();
                } catch {}
              }, 0);
              return;
            }

            try {
              ref.balloon.open();
            } catch {}
          }}
        />
      );
    };

    const renderClusterer = (items, colorKey, variant) => {
      if (!items.length) return null;

      const isFaded = variant === "faded";
      const opacity = isFaded ? 0.3 : 1;

      return (
        <Clusterer
          key={`ock-cluster-${colorKey}-${variant}`}
          instanceRef={(cl) => {
            if (!cl) return;
            if (cl.__gmBound) return;
            cl.__gmBound = true;

            cl.events.add("click", (e) => {
              const map = mapRef.current;
              const target = e.get("target");
              if (!map || !target) return;

              const isCluster = typeof target.getGeoObjects === "function";
              if (!isCluster) return;

              isClusterZoomingRef.current = true;

              const bounds = target.getBounds?.();
              if (bounds) {
                map.setBounds(bounds, { checkZoomRange: true, zoomMargin: 50, duration: 300 });
              }

              e.stopPropagation?.();
              e.preventDefault?.();
            });
          }}
          options={{
            ...clustererOptionsBase,
            clusterIcons: [
              {
                href: makeClusterSvgUrl(OCK_COLORS[colorKey], opacity, 35),
                size: [35, 35],
                offset: [-18, -18],
              },
            ],
            clusterNumbers: [1000000],
            hasBalloon: false,
          }}
        >
          {items.map((p) => renderPlacemark(p, colorKey, opacity))}
        </Clusterer>
      );
    };

    return order.flatMap((colorKey) => {
      const items = ockGroups[colorKey] || [];
      if (!items.length) return [];

      const { bright, faded } = splitOckItemsByMatch(items);

      return [
        renderClusterer(bright, colorKey, "bright"),
        renderClusterer(faded, colorKey, "faded"),
      ].filter(Boolean);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ockGroups,
    ockCloneMode,
    canManageOck,
    isGuest,
    canCloneOck,
    splitOckItemsByMatch,
  ]);

  const YM_LOAD_BASE =
    "Map,Placemark,Polyline,control.ZoomControl,control.FullscreenControl,geoObject.addon.balloon,layout.ImageWithContent,geocode,suggest";

  const ymapsLoad = useMemo(
    () => (isGuest ? YM_LOAD_BASE : `${YM_LOAD_BASE},geoObject.addon.editor`),
    [isGuest]
  );

  const mapModules = useMemo(
    () => [
      "layout.ImageWithContent",
      "GeoObjectCollection",
      "geoObject.addon.balloon",
      "Placemark",
      "Polyline",
      "geocode",
      "suggest",
      ...(isGuest ? [] : ["geoObject.addon.editor"]),
    ],
    [isGuest]
  );

  const ymapsQuery = useMemo(
    () => ({
      apikey: "520ce6aa-6c0f-4297-9f4a-b0e939f299d1",
      mode: "debug",
      load: ymapsLoad,
    }),
    [ymapsLoad]
  );

  const onStationPick = (station) => {
    if (!station?.id) return;
    selectOrFocusStation(station.id);

    const poly = polygonsRef.current[station.id];
    if (poly) {
      try {
        const bounds = poly.geometry?.getBounds?.();
        if (bounds?.[0]) mapRef.current?.setCenter(bounds[0], 16);
      } catch {}
    } else {
      const raw = getStationCoordsRaw(station);
      const p = Array.isArray(raw?.[0]) ? raw[0] : null;
      if (p && mapRef.current?.setCenter) mapRef.current.setCenter(p, 16);
    }
  };

  return (
    <YMaps query={ymapsQuery}>
      <div className="mapLayout" style={{ "--toolsDrawerWidth": "340px" }}>
        {!isMobile && (
          <button
            type="button"
            className={`toolsToggle ${toolsOpen ? "toolsToggle--open" : ""}`}
            onClick={() => setToolsOpen((v) => !v)}
            aria-label={toolsOpen ? "Закрыть инструменты" : "Открыть инструменты"}
            title={toolsOpen ? "Закрыть инструменты" : "Открыть инструменты"}
          >
            {toolsOpen ? "◂" : "▸"}
          </button>
        )}

        <DropdownMenu
          ymapsApiRef={ymapsApiRef}
          setNewCoords={setNewCoords}
          mapRef={mapRef}
          calculateDistances={calculateDistances}
          setClosestCoords={setClosestCoords}
          metroLineColors={metroLineColors}
          setActiveIds={setActiveIds}
          setVisible={setVisible}
          visible={visible}
          userState={userState}
          metroLineNumbers={metroLineNumbers}
          linecoordinates={linecoordinates}
          coordinates={coordinates}
          toggleActiveStation={toggleActiveStation}
          toggleActiveLine={toggleActiveLine}
          activeIds={activeIds}
          canViewBuildings={canViewBuildings}
          canUseNavigation={canUseNavigation}
          mainOck={mainOck}
          canViewOck={canViewOck}
          canManageOck={canManageOck}
          canViewArchiveBuildings={canViewArchiveBuildings}
          tasksOpen={tasksOpen}
          setTasksOpen={setTasksOpen}
          onYandexPoint={onYandexPoint}
          onStationPick={onStationPick}
          mobileToolsOpen={mobileToolbarOpen}
          onMobileToolsToggle={toggleMobileToolbar}
        />

        {mapRef.current && (
          <>
            <ToolsDrawer
              open={toolsDrawerOpen}
              width={340}
              mapRef={mapRef}
              onClose={() => (isMobile ? setMobileToolsDrawerOpen(false) : setToolsOpen(false))}
            >
              {hasOnlyAdd && (
                <ToolItem
                  title="Панель рисования"
                  open={!!toolOpen[TOOL_SECTIONS.DRAW]}
                  onToggle={() => toggleTool(TOOL_SECTIONS.DRAW)}
                  rightHint="Line/Station"
                >
                  <DrawPanel
                    embedded
                    forceCollapsed={false}
                    mapRef={mapRef}
                    metroLineNumbers={metroLineNumbers}
                    mode={mode}
                    setMode={setMode}
                    onSave={postEntity}
                    isOckPicking={isOckPicking}
                    username={username}
                    activateExclusiveTool={activateExclusiveTool}
                    releaseExclusiveTool={releaseExclusiveTool}
                    registerExclusiveToolStop={registerExclusiveToolStop}
                    unregisterExclusiveToolStop={unregisterExclusiveToolStop}
                    activeExclusiveTool={activeExclusiveTool}
                    isToolBlocked={isToolBlocked}
                    exclusiveTools={EXCLUSIVE_TOOLS}
                  />
                </ToolItem>
              )}

              {canManageOck && (
                  <ToolItem
                    title="ОСК"
                    open={!!toolOpen[TOOL_SECTIONS.OCK]}
                    onToggle={() => toggleTool(TOOL_SECTIONS.OCK)}
                    rightHint="Point"
                  >
                    <DrawPointOCKPanel
                      embedded
                      forceCollapsed={false}
                      mapRef={mapRef}
                      ymapsApiRef={ymapsApiRef}
                      nearbySources={{
                        stations: coordinates.details || [],
                        buildings: buildingscoord.details || [],
                        hermodoors: hermodoorcoord.details || [],
                      }}
                      onPickingChange={setIsOckPicking}
                      onCreated={(createdPoint) => {
                        console.log("Точка ОСК создана:", createdPoint);
                        fetchData("points-ock", setPointsOckCoord);
                      }}
                      activateExclusiveTool={activateExclusiveTool}
                      releaseExclusiveTool={releaseExclusiveTool}
                      registerExclusiveToolStop={registerExclusiveToolStop}
                      unregisterExclusiveToolStop={unregisterExclusiveToolStop}
                      activeExclusiveTool={activeExclusiveTool}
                      isToolBlocked={isToolBlocked}
                      exclusiveTools={EXCLUSIVE_TOOLS}
                      isMobile={isMobile}
                      existingOckPoints={pointsOckCoord.details || []}
                      duplicateRadiusMeters={1}
                    />
                  </ToolItem>
              )}
                  
              {canViewOck && (
                  <ToolItem
                    title="Фильтры"
                    open={!!toolOpen[TOOL_SECTIONS.FILTERS]}
                    onToggle={() => toggleTool(TOOL_SECTIONS.FILTERS)}
                    rightHint="Filters"
                  >
                    <FiltersPanel
                      embedded
                      forceCollapsed={false}
                      mapRef={mapRef}
                      ymapsApiRef={ymapsApiRef}
                      onFiltersChange={(filters) => {
                        console.log("filters changed:", filters);
                      }}
                      onOckMatchChange={handleOckMatchChange}
                    />
                  </ToolItem>
              )}

              {canViewArchiveBuildings && (
                <ToolItem
                  title="Архивные строения"
                  open={!!toolOpen[TOOL_SECTIONS.ARCHIVE_BUILDINGS]}
                  onToggle={() => toggleTool(TOOL_SECTIONS.ARCHIVE_BUILDINGS)}
                  rightHint="Archive"
                >
                  <ArchiveBuildingsPanel
                    buildings={archiveBuildingsCoord.details || []}
                    mapRef={mapRef}
                    ymapsApiRef={ymapsApiRef}
                    canManage={canManageArchiveBuildings}
                    onChanged={() => fetchData("archive-buildings", setArchiveBuildingsCoord)}
                    requestedBuildingId={archiveBuildingEditRequest}
                    onRequestedBuildingHandled={clearArchiveBuildingEditRequest}
                  />
                </ToolItem>
              )}
            </ToolsDrawer>
          </>
        )}

        <div className={"mapArea"}>
          {ockCloneMode.active && (
            <OckClonePanel
              open={ockCloneMode.active}
              sourcePoint={sourceClonePoint}
              points={ockCloneMode.points}
              saving={ockCloneSaving}
              onChangeInventory={handleClonePanelInventoryChange}
              onRemovePoint={removeCloneTempPoint}
              onFocusPoint={focusCloneTempPoint}
              onSave={saveOckCloneMode}
              onCancel={cancelOckCloneMode}
              isMobile={isMobile}
            />
          )}
          <YMap
            width="100vw"
            height="90vh"
            defaultState={{
              center: [55.74, 37.62],
              zoom: 11,
              type: "yandex#map",
              theme: "islands#dark",
            }}
            modules={mapModules}
            options={{
              yandexMapDisablePoiInteractivity: true,
              yandexMapAutoDark: true,
              suppressMapOpenBlock: true,
              suppressObsoleteBrowserNotifier: false,
            }}
            instanceRef={(ref) => {
              mapRef.current = ref;
              scheduleApplyVisibility();
            }}

            onLoad={(api) => {
              ymapsApiRef.current = api;
            }}
          >
            {yandexPoint?.coords && (
              <Placemark
                geometry={yandexPoint.coords}
                properties={{
                  balloonContentHeader: "Яндекс поиск",
                  balloonContentBody: `
                    <div style="margin-bottom:6px"><b>${(yandexPoint.address || "").replace(/</g, "&lt;")}</b></div>
                    ${
                      yandexPoint.description
                        ? `<div style="color:#444">${String(yandexPoint.description).replace(/</g, "&lt;")}</div>`
                        : ""
                    }
                    <div style="margin-top:8px;color:#888;font-size:12px">Запрос: ${(yandexPoint.query || "").replace(
                      /</g,
                      "&lt;"
                    )}</div>
                  `,
                  balloonContentFooter: "Подпись",
                }}
                options={{ draggable: false, preset: "islands#redDotIcon" }}
                instanceRef={(ref) => {
                  if (ref) {
                    try {
                      setTimeout(() => ref.balloon.open(), 0);
                    } catch {}
                  }
                }}
              />
            )}

            {newCoords && (
              <Placemark
                geometry={newCoords}
                properties={{
                  balloonContentBody: nearestLines
                    .slice()
                    .sort((a, b) => a.min_distance - b.min_distance)
                    .map(
                      (line, index) => `
                      <div style="margin-bottom:6px">
                        ${index + 1}. Линия:
                        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:#${
                          metroLineColors[line.line] ?? "000000"
                        };margin:0 6px;"></span>
                        <a data-id="${line.line_id}" data-coord="${line.closest_coords}" class="line-link">${line.name}</a>
                        — ${Number.isFinite(line.min_distance) ? line.min_distance.toFixed(2) : "∞"} км
                      </div>
                    `
                    )
                    .join(""),
                }}
                options={{ draggable: false, iconLayout: "default#image" }}
                instanceRef={(ref) => {
                  if (ref) {
                    ref.events.add("balloonopen", () => {
                      document.querySelectorAll(".line-link").forEach((link) => {
                        link.addEventListener("click", (event) => {
                          event.preventDefault();
                          const coordinates = (event.target.dataset.coord || "").split(",").map(Number);
                          if (coordinates.length === 2 && coordinates.every((n) => !Number.isNaN(n))) {
                            setClosestCoords(coordinates);
                          }
                        });
                      });
                    });
                  }
                }}
              />
            )}

            {closestCoords && newCoords && (
              <Polyline
                geometry={[newCoords, closestCoords]}
                options={{ strokeColor: "8D5B2D", opacity: 0.8, strokeWidth: 8 }}
              />
            )}

            <TypeSelector options={{ panoramasItemMode: "off" }} />
            <RulerControl options={{ position: { top: "10px", left: "14vw" } }} />

            <ObjectManager
              options={{ clusterize: false, gridSize: 150 }}
              features={null}
              objects={{ openBalloonOnClick: true, preset: "islands#greenDotIcon" }}
            />

            {linesection_sq}
            {wayouts}
            {crossways}
            {depo}
            {metrobridge}
            {station_sq}
            {hermodors}
            {pointsOck}
            {canViewArchiveBuildings && (
              <ArchiveBuildingsLayer
                buildings={archiveBuildingsCoord.details || []}
                visible={
                  visible.length === 0 ||
                  visible.includes("archiveBuildings") ||
                  visible.includes("withDocs")
                }
                canManage={canManageArchiveBuildings}
                onCorrectPosition={startArchiveBuildingPositionCorrection}
              />
            )}

            {canViewBuildings && (
              <Clusterer
                instanceRef={(ref) => {
                  buildingsClustererRef.current = ref;
                  scheduleApplyVisibility();
                }}
                options={{
                  preset: "islands#blueClusterIcons",
                  gridSize: 60,
                  clusterDisableClickZoom: false,
                  clusterOpenBalloonOnClick: false,
                }}
              >
                {(buildingscoord.details || []).map((b) => {
                  if (typeof b?.lat !== "number" || typeof b?.long !== "number") return null;
                  const key = b.id ?? `b-${b.name}`;
                  const btnId = `gm-copy-bld-cl-${b.id}`;

                  return (
                    <Placemark
                      key={key}
                      geometry={[b.lat, b.long]}
                      properties={{
                        balloonContentHeader: `<a href='/buildings/${b.id}'>${b.name}</a>`,
                        balloonContentBody: b.documentation ?? "",
                        balloonContentFooter: makeCopyBtnHtml(btnId),
                      }}
                      options={{
                        preset: "islands#blueHomeIcon",
                        draggable: false,
                        opacity: 1,
                      }}
                      instanceRef={(ref) => {
                        if (ref) {
                          attachCopyButton(btnId, () => [[b.lat, b.long]], false);
                        }

                        setRef("buildings", String(b.id ?? key), ref, {
                          hasDocs: hasDocs(b),
                          archiveLen: getArchiveLen(b),
                        });
                      }}
                    />
                  );
                })}
              </Clusterer>
            )}
          </YMap>
        </div>
      </div>
    </YMaps>
  );
};

export { MapPage };
